/**
 * PostHog / product flag keys — must match the dashboard.
 *
 * The course-page switch and the two checkout switches are gated separately so
 * they can be rolled out independently. A student who has *already* opted in
 * keeps seeing both switches whatever the flags resolve to — see
 * `useAutoLessonAnalysisCoursePage` / `useAutoLessonAnalysisCheckout`.
 */
export const AUTO_LESSON_ANALYSIS_COURSE_PAGE_FEATURE_FLAG =
  'auto_lesson_analysis_course_page';

/** Gates both checkout switches (one-time top-up and monthly plans). */
export const AUTO_LESSON_ANALYSIS_CHECKOUT_FEATURE_FLAG =
  'auto_lesson_analysis_checkout';

/**
 * Minutes of lesson credit one lesson consumes. Credits are stored in minutes
 * (1 credit === 1 minute), so a lesson costs 60 credits.
 */
export const LESSON_MINUTES = 60;

/**
 * Minutes of lesson credit deducted for each lesson that gets analysed by
 * Tutopiya AI. Must match the backend constant — checkout prices the add-on
 * from it and the backend deducts it after each lesson is analysed.
 */
export const AUTO_ANALYSIS_MINUTES_PER_LESSON = 15;

/**
 * PostHog flag gating the redesigned enrol popup: the slim collapsible lesson
 * analysis card with its price beside the switch, and the Course Fee / Total
 * Fees split on the monthly plans tab. Display only — pricing is unchanged.
 */
export const ENROL_POPUP_REDESIGN_LESSON_ANALYSIS_FEATURE_FLAG =
  'enrol-popup-redesign-lesson-analysis';

/**
 * Accepts both a multivariate flag ('test' variant) and a plain boolean flag,
 * so the flag can be set up either way in PostHog. 'control' stays disabled.
 */
export const isEnrolPopupRedesignVariantEnabled = (
  variant: string | boolean | undefined
): boolean => variant === 'test' || variant === true;

/**
 * Which of the three auto analysis switches the student saw or used. Sent with
 * every auto analysis event so the course-page control and the two checkout
 * controls can be compared separately.
 */
export type TAutoAnalysisSurface =
  | 'course_page'
  | 'checkout_one_time'
  | 'checkout_monthly';

/** The flag that gates each surface, so events can stamp the right variant. */
export const AUTO_ANALYSIS_SURFACE_FEATURE_FLAGS: Record<
  TAutoAnalysisSurface,
  string
> = {
  course_page: AUTO_LESSON_ANALYSIS_COURSE_PAGE_FEATURE_FLAG,
  checkout_one_time: AUTO_LESSON_ANALYSIS_CHECKOUT_FEATURE_FLAG,
  checkout_monthly: AUTO_LESSON_ANALYSIS_CHECKOUT_FEATURE_FLAG,
};
